import type { SalesOrderPayment as APISalesOrderPayment } from '../../api';
import type { SalesOrder, SalesOrderPayment, User } from '../../types/sales.types';

const PAYMENT_STATUSES = new Set<SalesOrderPayment['status']>([
  'COMPLETED',
  'PENDING',
  'SCHEDULED',
  'FAILED',
  'CANCELLED',
  'REFUNDED',
]);

const PAYMENT_TYPES = new Set<SalesOrderPayment['paymentType']>(['ITEM', 'ORDER']);

function mapProcessedBy(processor: APISalesOrderPayment['processedBy'] | undefined): User {
  const firstName = processor?.firstName ?? '';
  const lastName = processor?.lastName ?? '';
  const displayName = `${firstName} ${lastName}`.trim() || processor?.email || '—';
  const rawId = processor?.id;
  const numericId =
    typeof rawId === 'number' && Number.isFinite(rawId)
      ? rawId
      : typeof rawId === 'string' && /^\d+$/.test(rawId)
        ? Number(rawId)
        : 0;

  return {
    id: numericId,
    externalId: typeof rawId === 'string' ? rawId : undefined,
    username: displayName,
    email: processor?.email ?? '',
    firstName,
    lastName,
    role: 'USER',
    isActive: processor?.isActive ?? true,
    createdAt: new Date(processor?.createdAt ?? Date.now()),
    updatedAt: new Date(processor?.updatedAt ?? Date.now()),
  };
}

function asPaymentStatus(value: string | undefined): SalesOrderPayment['status'] {
  if (value != null && PAYMENT_STATUSES.has(value as SalesOrderPayment['status'])) {
    return value as SalesOrderPayment['status'];
  }
  return 'COMPLETED';
}

function asPaymentType(value: string | undefined): SalesOrderPayment['paymentType'] {
  if (value != null && PAYMENT_TYPES.has(value as SalesOrderPayment['paymentType'])) {
    return value as SalesOrderPayment['paymentType'];
  }
  return 'ORDER';
}

/**
 * Maps OpenAPI payment payload → local SalesOrderPayment (single source of truth).
 */
export function mapApiPaymentToLocal(
  apiPayment: APISalesOrderPayment | Record<string, unknown>,
  localOrder: SalesOrder,
): SalesOrderPayment {
  const payment = apiPayment as APISalesOrderPayment;
  return {
    id: Number(payment.id),
    order: localOrder,
    paymentAmount: Number(payment.paymentAmount),
    paymentMethod: payment.paymentMethod ?? payment.paymentMethodEntity?.code ?? 'CASH',
    paymentDate: new Date(payment.paymentDate),
    dueDate: payment.dueDate ? new Date(payment.dueDate) : undefined,
    installmentNumber: payment.installmentNumber,
    installmentTotal: payment.installmentTotal,
    status: asPaymentStatus(payment.status),
    paymentType: asPaymentType(payment.paymentType),
    itemReference: payment.itemReference,
    bankReference: payment.bankReference,
    qrTransactionId: payment.qrTransactionId,
    changeGiven: payment.changeGiven != null ? Number(payment.changeGiven) : undefined,
    paymentNotes: payment.paymentNotes,
    processedBy: mapProcessedBy(payment.processedBy),
    createdAt: new Date(payment.createdAt ?? payment.paymentDate ?? Date.now()),
  };
}
